import { supabase } from "@/integrations/supabase/client";

let timeoutId: NodeJS.Timeout | null = null;
let sessionAgeCheckId: NodeJS.Timeout | null = null;
let isTimeoutActive = false;
let isTimeoutDisabled = false;
let inactivitySignoutInitiated = false;

// Session age constants (14 days in milliseconds)
const FORCED_REAUTH_DURATION = 14 * 24 * 60 * 60 * 1000;
const SESSION_AGE_CHECK_INTERVAL = 24 * 60 * 60 * 1000; // Check daily

// Disable timeout on suspension/deactivation pages
export const disableSessionTimeout = () => {
  console.log('[SessionTimeout] Disabling session timeout');
  isTimeoutDisabled = true;
  if (timeoutId) {
    clearTimeout(timeoutId);
    timeoutId = null;
    isTimeoutActive = false;
  }
  if (sessionAgeCheckId) {
    clearTimeout(sessionAgeCheckId);
    sessionAgeCheckId = null;
  }
};

export const enableSessionTimeout = () => {
  console.log('[SessionTimeout] Enabling session timeout');
  isTimeoutDisabled = false;
  resetSessionTimeout();
};

const resetSessionTimeout = () => {
  // Don't set timeout if disabled
  if (isTimeoutDisabled) {
    console.log('[SessionTimeout] Timeout disabled - not setting');
    return;
  }

  // Clear existing timeout
  if (timeoutId) {
    clearTimeout(timeoutId);
  }

  // Set new timeout (48 hours)
  const timeoutDuration = 48 * 60 * 60 * 1000;
  timeoutId = setTimeout(async () => {
    console.log('[SessionTimeout] Session expired due to inactivity');
    
    // Check if we're on a suspension page - don't sign out if so
    const currentPath = window.location.pathname;
    if (currentPath === '/suspended' || currentPath === '/deactivated') {
      console.log('[SessionTimeout] On suspension page - not signing out');
      return;
    }

    isTimeoutActive = true;
    inactivitySignoutInitiated = true; // Set flag for attribution
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('[SessionTimeout] Error during timeout signout:', error);
    }
    isTimeoutActive = false;
    // Reset flag after a short delay
    setTimeout(() => {
      inactivitySignoutInitiated = false;
    }, 1000);
  }, timeoutDuration);

  console.log('[SessionTimeout] Session timeout set for 48 hours');
};

// Check if session age exceeds 14 days and force re-authentication
const checkSessionAge = async () => {
  const loginTimestamp = localStorage.getItem('session_login_timestamp');
  if (!loginTimestamp) return;

  const sessionAge = Date.now() - parseInt(loginTimestamp);
  
  if (sessionAge >= FORCED_REAUTH_DURATION) {
    console.log('[SessionTimeout] Session age exceeded 14 days - forcing re-authentication');
    
    // Check if we're on a suspension page - don't sign out if so
    const currentPath = window.location.pathname;
    if (currentPath === '/suspended' || currentPath === '/deactivated') {
      console.log('[SessionTimeout] On suspension page - not forcing re-auth');
      return;
    }

    // Set flag for forced re-authentication
    localStorage.setItem('forced_reauth_required', 'true');
    localStorage.removeItem('session_login_timestamp');
    
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('[SessionTimeout] Error during forced re-auth signout:', error);
    }
  }
};

// Start session age monitoring
const startSessionAgeMonitoring = () => {
  // Check immediately
  checkSessionAge();
  
  // Set up daily checks
  sessionAgeCheckId = setInterval(checkSessionAge, SESSION_AGE_CHECK_INTERVAL);
  console.log('[SessionTimeout] Session age monitoring started - checking every 24 hours');
};

// Record login timestamp for session age tracking
export const recordLoginTimestamp = () => {
  const timestamp = Date.now().toString();
  localStorage.setItem('session_login_timestamp', timestamp);
  localStorage.removeItem('forced_reauth_required');
  console.log('[SessionTimeout] Login timestamp recorded');
};

// Check if forced re-authentication is required
export const isForcedReauthRequired = () => {
  return localStorage.getItem('forced_reauth_required') === 'true';
};

// Check if the current signout was initiated by our inactivity timer
export const wasInactivitySignout = () => {
  return inactivitySignoutInitiated;
};

export const initializeSessionTimeout = () => {
  console.log('[SessionTimeout] Initializing session timeout system');
  
  // Reset timeout on user activity
  const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
  const resetTimeout = () => {
    if (!isTimeoutActive && !isTimeoutDisabled) {
      resetSessionTimeout();
    }
  };

  events.forEach(event => {
    document.addEventListener(event, resetTimeout, true);
  });

  // Initial timeout
  resetSessionTimeout();
  
  // Start session age monitoring
  startSessionAgeMonitoring();

  // Listen for page visibility changes
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && !isTimeoutDisabled) {
      resetSessionTimeout();
    }
  });

  return () => {
    console.log('[SessionTimeout] Cleaning up session timeout');
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    if (sessionAgeCheckId) {
      clearTimeout(sessionAgeCheckId);
      sessionAgeCheckId = null;
    }
    isTimeoutActive = false;
    
    events.forEach(event => {
      document.removeEventListener(event, resetTimeout, true);
    });
  };
};